import React, { useState, useEffect } from 'react';
import PickYourPlanSectionStyled from './PickYourPlanSection.styled';
import { useTranslation } from 'react-i18next';
import { PickYourPlanTile, PearlError } from 'framework';
import { useFormContext } from 'react-hook-form';
import { sortByPrice } from 'framework/constants/WebstorePlanTiers';
import { StripeService } from 'lib/apis/StripeServiceNew';
import { StorageService } from 'lib/StorageService';
import PearlConnectPlanTile from './PearlConnectPlanTile';

const PickYourPlanSection = ({ defaultPlanValue, pearlConnectPlanLayout = false }) => {
    const formFieldName = 'SelectedPlan';
    const { getValues } = useFormContext();
    const SelectedPlan = getValues('SelectedPlan');

    const { t } = useTranslation();

    const [selectedPlan, setSelectedPlan] = useState(defaultPlanValue);
    const [expandedPlan, setExpandedPlan] = useState(null);
    const [planList, setPlanList] = useState([]);
    const storageService = StorageService();

    useEffect(() => {
        const getStripeData = async () => {
            try {
                const stripeService = StripeService();
                const response = await stripeService.getStripeProducts();
                const result = await response?.data;
                storageService.set('stripeData', JSON.stringify(result));
                if (result) {
                    const parsedStripeData = result;
                    const Plans = parsedStripeData
                        .map((plan) => {
                            if (plan.billingFrequency === 'month' && Number(plan.price) >= 0) {
                                return plan?.planTier;
                            }
                            return null;
                        })
                        .filter((item) => item !== null);

                    const fetchedPlanList = Plans.map((plan) => {
                        const monthlyItem = parsedStripeData.find(
                            (data) => data.planTier === plan && data.billingFrequency === 'month'
                        );
                        const yearlyItem = parsedStripeData.find(
                            (data) => data.planTier === plan && data.billingFrequency === 'year'
                        );

                        if (!monthlyItem) {
                            return null;
                        }

                        const monthlyPrice = parseFloat(monthlyItem.price);
                        const yearlyPrice = yearlyItem ? parseFloat(yearlyItem.price) : null;

                        // display price: floor(yearly / 12) when yearly exists, otherwise fall back to monthly
                        const effectiveMonthly = yearlyPrice
                            ? Math.floor(yearlyPrice / 12)
                            : monthlyPrice;

                        return {
                            id: monthlyItem.id,
                            value: String(effectiveMonthly),
                            title: monthlyItem.tierName,
                            period: 'Forms.PlanTier.Period',
                            altPrice: `Forms.PlanTier.AltPrice.${plan}`,
                            altPriceLink: `Forms.PlanTier.AltPriceLink.${plan}`,
                            description: `Forms.PlanTier.Description.${plan}`,
                            benefits: `Forms.PlanTier.Benefits.${plan}`,
                            isPopular: `Forms.PlanTier.Popular.${plan}`,
                            planInfoBtn: `Forms.PlanTier.PlanInfoButton.${plan}`,
                            planInfo: `Forms.PlanTier.PlanInfo.${plan}`,
                            tierName: monthlyItem.planTier,
                        };
                    }).filter((item) => item !== null);

                    setPlanList(sortByPrice(fetchedPlanList));
                }
            } catch (error) {
                console.error("Couldn't receive stripe data from DB");
            }
        };
        getStripeData();
    }, []);

    const handleToggleClick = (id) => {
        setExpandedPlan(expandedPlan === id ? null : id);
    };

    const pearlDescriptionFor = (tierName) => {
        const key = `Forms.PlanTier.PearlConnect.Description.${tierName}`;
        const copy = t(key, { defaultValue: '' });
        if (copy && copy !== key) {
            return copy;
        }
        return t(`Forms.PlanTier.Description.${tierName}`);
    };

    const pearlTaglineFor = (tierName) => {
        const key = `Forms.PlanTier.PearlConnect.Tagline.${tierName}`;
        const copy = t(key, { defaultValue: '' });
        return copy !== key ? copy : '';
    };

    const pearlBenefitsFor = (tier, benefitsKey) => {
        const pearlList = t(`Forms.PlanTier.PearlConnect.Benefits.${tier.tierName}`, {
            returnObjects: true,
        });
        if (Array.isArray(pearlList) && pearlList.length > 0) {
            return pearlList;
        }
        return t(benefitsKey, { returnObjects: true });
    };

    return (
        <PickYourPlanSectionStyled
            className={pearlConnectPlanLayout ? 'pearl-connect-plans' : undefined}
        >
            <label className="subtitleOne">{t('Forms.PlanTier.SubtitleOne')}</label>
            {!SelectedPlan && <PearlError name="SelectedPlan"></PearlError>}
            <div className="price-tier-wrapper">
                <div className="boxes">
                    {planList?.map((tier) => {
                        const benefitsRaw = pearlConnectPlanLayout
                            ? pearlBenefitsFor(tier, tier.benefits)
                            : t(tier.benefits, { returnObjects: true });
                        const benefits = Array.isArray(benefitsRaw) ? benefitsRaw : [];
                        const displayTitle = t(`Forms.PlanTier.DisplayName.${tier.tierName}`, {
                            defaultValue: t(tier.title),
                        });
                        const priceLabel = `$${t(tier.value)}${t(tier.period)}`;
                        const popularVal = t(`Forms.PlanTier.Popular.${tier.tierName}`);
                        const isMostPopular = popularVal === true || popularVal === 'true';

                        if (pearlConnectPlanLayout) {
                            return (
                                <PearlConnectPlanTile
                                    key={tier.id}
                                    title={displayTitle}
                                    priceLabel={priceLabel}
                                    tagline={pearlTaglineFor(tier.tierName)}
                                    description={pearlDescriptionFor(tier.tierName)}
                                    benefits={benefits}
                                    planId={tier.id}
                                    planTier={tier.tierName}
                                    formFieldName={formFieldName}
                                    isSelected={selectedPlan === tier.tierName}
                                    onSelect={() => setSelectedPlan(tier.tierName)}
                                    isMostPopular={isMostPopular}
                                />
                            );
                        }

                        return (
                            <PickYourPlanTile
                                title={t(tier.title)}
                                price={priceLabel}
                                period={t(tier.period)}
                                value={t(tier.value)}
                                altPrice={t(tier.altPrice)}
                                altPriceLink={t(tier.altPriceLink)}
                                description={t(tier.description)}
                                benefits={benefits}
                                isPopular={t(tier.isPopular)}
                                key={t(tier.id)}
                                planId={t(tier.id)}
                                planInfo={t(tier.planInfo)}
                                formFieldName={formFieldName}
                                onClick={() => setSelectedPlan(tier.tierName)}
                                isSelected={selectedPlan === tier.tierName}
                                isOpen={expandedPlan === tier.id}
                                onToggleClick={() => handleToggleClick(tier.id)}
                                planInfoBtn={t(tier.planInfoBtn)}
                                planTier={tier.tierName}
                            />
                        );
                    })}
                </div>
                {planList?.length > 0 && (
                    <div className="footerText">{t('Forms.PlanTier.FooterText')}</div>
                )}
            </div>
        </PickYourPlanSectionStyled>
    );
};

export default PickYourPlanSection;
